import React, { useState, useRef } from 'react';
import { ArrowUpTrayIcon, DocumentArrowDownIcon, XMarkIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { api } from '../../../shared/services/api';
import { CHILDREN } from '../../../shared/services/api/endpoints';
import { ENV_CONFIG } from '../../../config/environment';

export default function ImportChildrenModal({ isOpen, onClose, onImportSuccess }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'importing' | 'done'
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null); // { rows: [], summary: { total, valid, invalid } }
  const [importResult, setImportResult] = useState(null); // { successCount, failedCount, results: [] }
  const [filePath, setFilePath] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showFailedRows, setShowFailedRows] = useState(false);

  const fileInputRef = useRef(null);

  /* ---------------------- Reset state on close ---------------------- */
  const handleClose = () => {
    setStep('upload');
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    setFilePath('');
    setError('');
    setIsLoading(false);
    setIsDragging(false);
    setShowFailedRows(false);
    onClose();
  };

  /* ---------------------- File selection ---------------------- */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext === 'xlsx' || ext === 'xls') {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Only .xlsx or .xls files are accepted.');
      }
    }
  };

  /* ---------------------- Download sample ---------------------- */
  const handleDownloadSample = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const baseUrl = ENV_CONFIG.API_BASE_URL.replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/${CHILDREN.IMPORT_SAMPLE}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error('Server returned an error');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sample_children_import.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download sample file. Please try again.');
    }
  };

  /* ---------------------- Upload & preview ---------------------- */
  const handleUploadAndPreview = async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.upload(CHILDREN.IMPORT_UPLOAD, formData);
      const data = response?.data?.data || response?.data;

      setFilePath(data?.filePath || data?.file_path || '');
      setPreviewData({
        rows: data?.rows || data?.preview || [],
        summary: data?.summary || {
          total: data?.total || 0,
          valid: data?.valid || 0,
          invalid: data?.invalid || 0,
        },
      });
      setStep('preview');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Upload failed. Please check your file and try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------------- Confirm import ---------------------- */
  const handleConfirmImport = async () => {
    setStep('importing');
    setError('');
    try {
      const response = await api.post(CHILDREN.IMPORT_CONFIRM, { filePath });
      const data = response?.data?.data || response?.data;

      setImportResult({
        successCount: data?.successCount ?? data?.success ?? 0,
        failedCount: data?.failedCount ?? data?.failed ?? 0,
        results: data?.results || data?.errors || [],
      });
      setStep('done');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Import failed. Please try again.';
      setError(msg);
      setStep('preview');
    }
  };

  /* ---------------------- Done / success ---------------------- */
  const handleDoneClose = () => {
    if (importResult?.successCount > 0 && onImportSuccess) {
      onImportSuccess();
    } else {
      handleClose();
    }
  };

  if (!isOpen) return null;

  /* ===================== RENDER ===================== */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* ---- Header ---- */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
              <ArrowUpTrayIcon className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">Import Children (Excel)</h2>
            {console.log('Current step:', step)}
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ---- Body (scrollable) ---- */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ===== STEP: UPLOAD ===== */}
          {step === 'upload' && (
            <>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Upload Excel File</p>

                {/* Drag-drop area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
                  }`}
                >
                  <ArrowUpTrayIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  {selectedFile ? (
                    <div>
                      <p className="text-sm font-medium text-green-700">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(selectedFile.size / 1024).toFixed(1)} KB — click to change
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Drag & drop your Excel file here
                      </p>
                      <p className="text-xs text-gray-500 mt-1">or click to browse (.xlsx, .xls)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={handleFileChange}
                />
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-blue-800 mb-2">Excel Format</p>
                <p className="text-xs text-blue-700 mb-2">
                  The file must contain the following columns (in order):
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    'Name',
                    'Date of Birth (YYYY-MM-DD)',
                    'Weight (kg)',
                    'Father Name',
                    'Address',
                    'Mobile Number',
                  ].map((col) => (
                    <div key={col} className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                      <span className="text-xs text-blue-700">{col}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ===== STEP: PREVIEW ===== */}
          {step === 'preview' && previewData && (
            <>
              {/* Summary badges */}
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg font-medium">
                  Total: {previewData.summary.total}
                </span>
                <span className="px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg font-medium">
                  Valid: {previewData.summary.valid}
                </span>
                <span className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg font-medium">
                  Invalid: {previewData.summary.invalid}
                </span>
              </div>

              {/* Preview table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {['#', 'Name', 'DOB', 'Weight', 'Father Name', 'Address', 'Mobile', 'Status'].map(
                          (h) => (
                            <th
                              key={h}
                              className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {previewData.rows.map((row, idx) => {
                        const isValid = row.isValid !== false && !row.errors?.length;
                        return (
                          <tr key={idx} className={isValid ? '' : 'bg-red-50'}>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{idx + 1}</td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{row.name || row.Name || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.dateOfBirth || row.dob || row['Date of Birth'] || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.weight || row.weightKg || row['Weight (kg)'] || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.fatherName || row['Father Name'] || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate">{row.address || row.Address || '—'}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{row.mobile || row.mobileNumber || row['Mobile Number'] || '—'}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {isValid ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                  Valid
                                </span>
                              ) : (
                                <div>
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                    Invalid
                                  </span>
                                  {row.errors?.[0] && (
                                    <p className="text-xs text-red-600 mt-0.5 max-w-[140px] truncate" title={row.errors[0]}>
                                      {row.errors[0]}
                                    </p>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {previewData.summary.valid === 0 && (
                <p className="text-sm text-red-600 font-medium">
                  No valid rows found. Please fix the file and re-upload.
                </p>
              )}
            </>
          )}

          {/* ===== STEP: IMPORTING ===== */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-600 font-medium">Processing import...</p>
              <p className="text-sm text-gray-400">Please wait while we import the children records.</p>
            </div>
          )}

          {/* ===== STEP: DONE ===== */}
          {step === 'done' && importResult && (
            <div className="space-y-4">
              <div className="flex flex-col items-center py-6 gap-3">
                <CheckCircleIcon className="w-14 h-14 text-green-500" />
                <h3 className="text-xl font-semibold text-gray-800">Import Complete!</h3>
              </div>

              <div className="flex justify-center gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{importResult.successCount}</p>
                  <p className="text-sm text-gray-500 mt-1">Successfully imported</p>
                </div>
                {importResult.failedCount > 0 && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-500">{importResult.failedCount}</p>
                    <p className="text-sm text-gray-500 mt-1">Failed</p>
                  </div>
                )}
              </div>

              {importResult.failedCount > 0 && importResult.results?.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowFailedRows((p) => !p)}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    <ExclamationCircleIcon className="w-4 h-4" />
                    {showFailedRows ? 'Hide' : 'Show'} failed rows ({importResult.failedCount})
                  </button>

                  {showFailedRows && (
                    <div className="mt-3 border border-red-200 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-red-50 sticky top-0">
                            <tr>
                              {['Row', 'Name', 'Reason'].map((h) => (
                                <th
                                  key={h}
                                  className="px-3 py-2 text-left text-xs font-semibold text-red-700 border-b border-red-200"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-red-100">
                            {importResult.results
                              .filter((r) => !r.success && r.status !== 'success')
                              .map((r, idx) => (
                                <tr key={idx}>
                                  <td className="px-3 py-2 text-gray-500">{r.row || idx + 1}</td>
                                  <td className="px-3 py-2 text-gray-700">{r.name || r.data?.name || '—'}</td>
                                  <td className="px-3 py-2 text-red-600 text-xs">{r.error || r.reason || r.message || '—'}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ---- Error alert ---- */}
          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* ---- Footer ---- */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          {/* Left: Download sample (upload step only) */}
          {step === 'upload' && (
            <button
              onClick={handleDownloadSample}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              Download Sample File
            </button>
          )}
          {step !== 'upload' && <span />}

          {/* Right: Action buttons */}
          <div className="flex items-center gap-3">
            {step === 'upload' && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadAndPreview}
                  disabled={!selectedFile || isLoading}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {isLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="w-4 h-4" />
                      Upload &amp; Preview
                    </>
                  )}
                </button>
              </>
            )}

            {step === 'preview' && (
              <>
                <button
                  onClick={() => { setStep('upload'); setPreviewData(null); setError(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmImport}
                  disabled={!previewData || previewData.summary.valid === 0}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Confirm Import ({previewData?.summary?.valid || 0} rows)
                </button>
              </>
            )}

            {step === 'done' && (
              <button
                onClick={handleDoneClose}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
